import { useState, useEffect, useCallback } from "react";

const API = "https://tcio-api.onrender.com";
const AUTH = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('tcio_token')}` } });

// ── TCIO light palette (spec-exact)
const C = {
  canvas: "#F4F8FB", surface: "#FFFFFF", ink: "#22303E", sec: "#5D6B7A",
  blue: "#4479B0", deep: "#336090", soft: "#85AACF", act: "#0878E8", border: "#D8E3EE",
  purple: "#7C5CE7", purpleBg: "#F1EEFF", teal: "#18A6B8", tealBg: "#E8F7F9",
  green: "#16A36A", greenBg: "#EAF8F1", amber: "#E99A18", amberBg: "#FFF6E5",
  red: "#E5484D", redBg: "#FDECEE",
};

const LOC_ACCENT = {
  DTX: { label: "Detox", color: C.amber, bg: C.amberBg },
  RTC: { label: "Residential", color: C.blue, bg: "#EAF1F8" },
  PHP: { label: "PHP", color: C.teal, bg: C.tealBg },
  IOP: { label: "IOP", color: C.purple, bg: C.purpleBg },
};

function fmtMoney(n) {
  if (n === null || n === undefined || isNaN(n)) return "--";
  const v = Number(n);
  if (Math.abs(v) >= 1000000) return "$" + (v / 1000000).toFixed(2) + "M";
  if (Math.abs(v) >= 1000) return "$" + Math.round(v / 1000).toLocaleString() + "K";
  return "$" + Math.round(v).toLocaleString();
}
function fmtMoneyFull(n) {
  if (n === null || n === undefined || isNaN(n)) return "--";
  return "$" + Number(n).toLocaleString("en-US", { maximumFractionDigits: 0 });
}
function fmtPct(n) {
  if (n === null || n === undefined || isNaN(n)) return "--";
  return Math.round(n) + "%";
}

// Value state helper: Loading / unavailable / actual value. Never fake zeros.
function V({ loading, value, render }) {
  if (loading) return <span style={{ color: C.soft }}>...</span>;
  if (value === null || value === undefined) return <span style={{ color: C.sec }}>--</span>;
  return render ? render(value) : value;
}

export default function ExecutiveIntelligence({ nav }) {
  const [facilities, setFacilities] = useState([]);
  const [facility, setFacility] = useState("all");
  const [periodDays, setPeriodDays] = useState(30);
  const [snapshots, setSnapshots] = useState({});
  const [beds, setBeds] = useState({});
  const [collections, setCollections] = useState(null);
  const [money, setMoney] = useState(null);
  const [context, setContext] = useState(null);
  const [weeklyBilled, setWeeklyBilled] = useState(null);
  const [unbilled, setUnbilled] = useState(null); // { facility_id, week_start, loading, data }
  const [bcView, setBcView] = useState('facility'); // 'facility' | 'week'
  const [bcWeeksN, setBcWeeksN] = useState(4);
  const [bcModal, setBcModal] = useState(null); // facility_id for the weekly-detail modal
  const [bcShowAll, setBcShowAll] = useState(false);
  const [dueCounts, setDueCounts] = useState(null);
  const [arSummary, setArSummary] = useState(null);
  const [facColl, setFacColl] = useState(null);
  const [attn, setAttn] = useState({});
  const [loading, setLoading] = useState(true);
  const [updatedAt, setUpdatedAt] = useState(null);

  const isExec = (() => {
    try { return ['ceo','admin'].includes(JSON.parse(atob(localStorage.getItem('tcio_token').split('.')[1])).role); }
    catch { return false; }
  })();

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const facRes = await fetch(`${API}/api/facilities`, AUTH());
      const facData = await facRes.json();
      // Sandbox facilities are demo environments - never part of real executive forecasts.
      const facs = (facData.ok ? facData.data : []).filter(f => !f.is_sandbox);
      setFacilities(facs);

      const snapMap = {}, bedMap = {};
      await Promise.all(facs.map(async (f) => {
        try {
          const [sR, bR] = await Promise.all([
            fetch(`${API}/api/snapshot/${f.id}/latest`, AUTH()),
            fetch(`${API}/api/beds/${f.id}`, AUTH()),
          ]);
          const sD = await sR.json(); const bD = await bR.json();
          if (sD.ok && sD.data) snapMap[f.id] = sD.data;
          if (bD.ok) bedMap[f.id] = bD.data;
        } catch (e) { /* facility fetch failed - stays absent, renders as unavailable */ }
      }));
      setSnapshots(snapMap);
      setBeds(bedMap);
      fetch(`${API}/api/collections/facility-summary`, AUTH())
        .then(r => r.json()).then(d => setFacColl(d.ok ? d.data : null)).catch(() => setFacColl(null));
      const attnMap = {};
      await Promise.all(facs.map(async (f) => {
        try {
          const r = await fetch(`${API}/api/emr/census-attention?facility_id=${f.id}`, AUTH());
          const d = await r.json();
          if (d.ok && d.attention) {
            let items = 0;
            Object.values(d.attention).forEach(v => { items += Array.isArray(v) ? v.length : (v && typeof v === 'object' ? Object.keys(v).length : 0); });
            attnMap[f.id] = { patients: Object.keys(d.attention).length, items };
          }
        } catch (e) { /* non-live or failed - absent renders as -- */ }
      }));
      setAttn(attnMap);
      setUpdatedAt(new Date());
    } catch (e) { console.error("exec loadAll:", e); }
    setLoading(false);
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Scoped fetches: rerun on facility change
  useEffect(() => {
    const q = facility === "all" ? "" : `?facilityId=${facility}`;
    fetch(`${API}/api/collections/summary${q}`, AUTH())
      .then(r => r.json()).then(d => setCollections(d.ok ? d.data : null)).catch(() => setCollections(null));
    const pFrom = new Date(Date.now() - periodDays * 86400000).toISOString().slice(0, 10);
    const cq = `?from=${pFrom}${facility === "all" ? "" : `&facility=${facility}`}`;
    fetch(`${API}/api/billing-census/weekly-summary`, AUTH())
      .then(r => r.json()).then(d => setWeeklyBilled(d.ok ? d.data : null)).catch(() => setWeeklyBilled(null));
    fetch(`${API}/api/executive/context${cq}`, AUTH())
      .then(r => r.json()).then(d => setContext(d.ok ? d : null)).catch(() => setContext(null));
    const dq = facility === "all" ? "" : `?facilityId=${facility}`;
    fetch(`${API}/api/due-counts${dq}`, AUTH())
      .then(r => r.json()).then(d => setDueCounts(d.ok ? d.counts : null)).catch(() => setDueCounts(null));
    if (isExec) {
      fetch(`${API}/api/money-owed?facilityId=${facility}`, AUTH())
        .then(r => r.json()).then(d => setMoney(d.ok ? d : null)).catch(() => setMoney(null));
      const aq = facility === "all" ? "" : `?facilityId=${facility}`;
      fetch(`${API}/api/ar/summary${aq}`, AUTH())
        .then(r => r.json()).then(d => setArSummary(d.ok ? d : null)).catch(() => setArSummary(null));
    }
  }, [facility, periodDays, isExec]);

  // ── Census aggregation from Bed Board truth (same math as Bed Board reads)
  const stats = (() => {
    const ids = facility === "all" ? facilities.map(f => f.id) : [facility];
    let pts = 0, tot = 0, rev = 0, overflowBeds = 0, overflowOcc = 0;
    const loc = { DTX: { c: 0, t: 0 }, RTC: { c: 0, t: 0 }, PHP: { c: 0, t: 0 }, IOP: { c: 0, t: 0 } };
    let haveData = false;
    ids.forEach(fid => {
      const s = snapshots[fid]; const bs = beds[fid] || [];
      if (s) {
        haveData = true;
        pts += s.total_patients || 0;
        rev += parseFloat(s.daily_revenue_projected || 0);
        loc.DTX.c += s.dtx_census || 0; loc.RTC.c += s.rtc_census || 0;
        loc.PHP.c += s.php_census || 0; loc.IOP.c += s.iop_census || 0;
      }
      bs.forEach(b => {
        if (b.bed_type === 'program') return;
        if (b.bed_facility_id && b.bed_facility_id !== fid) return; // shared-in bed: capacity belongs to the owner facility
        if (b.is_overflow) { if (b.patient_name) overflowOcc++; overflowBeds++; return; } // purchased beds: occupants in census, beds not in capacity
        if (loc[b.loc]) loc[b.loc].t++;
        tot++;
      });
    });
    return { pts, tot, open: tot - pts, fill: tot > 0 ? (pts / tot) * 100 : null, rev, loc, haveData, overflowBeds, overflowOcc };
  })();

  // Occupancy target from data (never hardcoded): single facility = its target; all = HQ default
  const target = (() => {
    if (!context || !context.targets) return null;
    if (facility !== "all" && context.targets[facility]) return context.targets[facility].occupancy_target;
    const vals = Object.values(context.targets).map(t => t.occupancy_target).filter(v => v != null);
    return vals.length ? vals[0] : null;
  })();

  const funnel = context ? context.funnel : null;
  const totals = money ? money.totals : null;
  const fightStalled = totals ? (totals.fight_billed || 0) + (totals.stalled_billed || 0) : null;

  return (
    <div className="ei-root" style={{ background: C.canvas, minHeight: "100vh", color: C.ink, fontFamily: "'Inter','Helvetica Neue',Arial,sans-serif" }}>
      <style>{`
        .ei-body { padding: 20px 24px; max-width: 1440px; margin: 0 auto; }
        .ei-kpis { display: grid; grid-template-columns: repeat(6, 1fr); gap: 12px; }
        .ei-card { background: ${C.surface}; border: 1px solid ${C.border}; border-radius: 10px; padding: 14px 16px; }
        .ei-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; flex-wrap: wrap; }
        @media (max-width: 1100px) { .ei-kpis { grid-template-columns: repeat(3, 1fr); } }
        @media (max-width: 700px)  { .ei-kpis { grid-template-columns: 1fr 1fr; } .ei-body { padding: 14px; } }
        @media (max-width: 460px)  { .ei-kpis { grid-template-columns: 1fr; } }
        .ei-row2 { display: grid; grid-template-columns: 3fr 2fr; gap: 12px; margin-top: 18px; }
        .ei-panel-title { font-size: 12px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: ${C.deep}; }
        .ei-panel-sub { font-size: 12px; color: ${C.sec}; margin-top: 2px; }
        .ei-tbl { width: 100%; border-collapse: collapse; margin-top: 12px; }
        .ei-tbl th { font-size: 10.5px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: ${C.sec}; text-align: left; padding: 6px 8px; border-bottom: 1px solid ${C.border}; }
        .ei-tbl td { font-size: 13px; padding: 9px 8px; border-bottom: 1px solid ${C.border}; }
        .ei-tbl tr:last-child td { border-bottom: none; }
        .ei-link { font-size: 12.5px; font-weight: 600; color: ${C.act}; cursor: pointer; background: none; border: none; padding: 0; }
        @media (max-width: 900px) { .ei-row2 { grid-template-columns: 1fr; } }
        .ei-row3 { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-top: 18px; }
        .ei-fin3 { display: grid; grid-template-columns: repeat(3, 1fr); gap: 14px; margin-top: 14px; }
        @media (max-width: 900px) { .ei-row3 { grid-template-columns: 1fr; } }
        @media (max-width: 520px) { .ei-fin3 { grid-template-columns: 1fr; } }
        .ei-agebar { display: flex; height: 14px; border-radius: 7px; overflow: hidden; margin-top: 12px; }
        .ei-agelbl { display: flex; gap: 4px; margin-top: 8px; flex-wrap: wrap; }
        .ei-tier { font-size: 10.5px; font-weight: 700; letter-spacing: 0.06em; padding: 3px 8px; border-radius: 5px; }
        .ei-brief-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(230px, 1fr)); gap: 10px; margin-top: 12px; }
      `}</style>

      <div className="ei-body">
        {/* ── HEADER */}
        <div className="ei-header">
          <div>
            <div style={{ fontSize: 22, fontWeight: 700, letterSpacing: "-0.01em" }}>Executive Intelligence</div>
            <div style={{ fontSize: 13, color: C.sec, marginTop: 2 }}>Real-time operational, clinical, and financial performance</div>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <select value={facility} onChange={e => setFacility(e.target.value)}
              style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, color: C.ink, fontSize: 13, padding: "8px 12px", minWidth: 200 }}>
              <option value="all">All Facilities</option>
              {facilities.map(f => <option key={f.id} value={f.id}>{f.org_name}</option>)}
            </select>
            <select value={periodDays} onChange={e => setPeriodDays(Number(e.target.value))}
              style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, color: C.ink, fontSize: 13, padding: "8px 12px" }}>
              <option value={7}>Last 7 Days</option>
              <option value={30}>Last 30 Days</option>
              <option value={90}>Last 90 Days</option>
            </select>
            <button onClick={loadAll}
              style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, color: C.deep, fontSize: 13, padding: "8px 14px", cursor: "pointer" }}>
              Refresh
            </button>
          </div>
        </div>
        <div style={{ fontSize: 12, color: C.sec, margin: "6px 0 18px" }}>
          {updatedAt ? `Last updated ${updatedAt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}` : "Loading..."}
        </div>

        {/* ── KPI STRIP */}
        <div className="ei-kpis">
          <div className="ei-card">
            <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: C.sec }}>Census / Occupancy</div>
            <div style={{ fontSize: 28, fontWeight: 700, marginTop: 6 }}>
              <V loading={loading} value={stats.haveData ? stats.fill : null} render={v => fmtPct(v)} />
            </div>
            <div style={{ fontSize: 12, color: C.sec, marginTop: 2 }}>
              {stats.haveData ? `${stats.pts} of ${stats.tot} beds` : "Awaiting census data"}
              {stats.haveData && stats.overflowBeds > 0 && ` · +${stats.overflowOcc}/${stats.overflowBeds} overflow`}
            </div>
            {target != null && stats.haveData && (
              <div style={{ fontSize: 12, marginTop: 4, color: stats.fill >= target ? C.green : C.amber }}>
                Target {fmtPct(target)}
              </div>
            )}
          </div>

          <div className="ei-card">
            <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: C.sec }}>Admissions ({periodDays} Days)</div>
            <div style={{ fontSize: 28, fontWeight: 700, marginTop: 6 }}>
              <V loading={!context && loading} value={funnel ? funnel.admitted : null} />
            </div>
            {funnel && (
              <>
                <div style={{ fontSize: 12, color: C.sec, marginTop: 2 }}>of {funnel.admit_goal} goal</div>
                {context && context.trends && context.trends.admits_prior_period > 0 && (() => {
                  const delta = Math.round(((funnel.admitted - context.trends.admits_prior_period) / context.trends.admits_prior_period) * 100);
                  return <div style={{ fontSize: 12, marginTop: 2, color: delta >= 0 ? C.green : C.amber }}>{delta >= 0 ? "+" : ""}{delta}% vs prior period</div>;
                })()}
                {funnel.calls > 0 && (
                  <div style={{ fontSize: 12, color: C.sec, marginTop: 4 }}>
                    {((funnel.admitted / funnel.calls) * 100).toFixed(1)}% call-to-admit
                  </div>
                )}
              </>
            )}
          </div>

          <div className="ei-card">
            <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: C.sec }}>Collected (MTD)</div>
            <div style={{ fontSize: 28, fontWeight: 700, marginTop: 6 }}>
              <V loading={!collections && loading} value={collections ? collections.collected_mtd : null} render={v => fmtMoneyFull(v)} />
            </div>
            {collections && collections.pace_vs_avg_pct !== null && (
              <div style={{ fontSize: 12, marginTop: 4, color: collections.pace_vs_avg_pct >= 80 ? C.green : collections.pace_vs_avg_pct >= 50 ? C.amber : C.red }}>
                {collections.pace_vs_avg_pct}% of trailing avg
              </div>
            )}
          </div>

          <div className="ei-card">
            <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: C.sec }}>30-Day Revenue Forecast</div>
            <div style={{ fontSize: 28, fontWeight: 700, marginTop: 6 }}>
              <V loading={loading} value={stats.haveData && stats.rev ? stats.rev * 30 : null} render={v => fmtMoneyFull(v)} />
            </div>
            <div style={{ fontSize: 12, color: C.sec, marginTop: 2 }} title="Current payer mix x collected rates, held for 30 days">
              payer mix x collected rates
            </div>
          </div>

          <div className="ei-card">
            <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: C.sec }}>Realistic A/R Value</div>
            <div style={{ fontSize: 28, fontWeight: 700, marginTop: 6, color: C.deep }}>
              {isExec
                ? <V loading={!money && loading} value={totals ? totals.realistic_value : null} render={v => fmtMoney(v)} />
                : <span style={{ color: C.sec, fontSize: 14 }}>Restricted</span>}
            </div>
            {isExec && totals && (
              <>
                <div style={{ fontSize: 12, color: C.sec, marginTop: 2 }}>of {fmtMoney(totals.open_billed)} billed</div>
                {fightStalled !== null && (
                  <div style={{ fontSize: 12, marginTop: 4, color: C.red }}>{fmtMoney(fightStalled)} Fight / Stalled</div>
                )}
              </>
            )}
          </div>

          <div className="ei-card">
            <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: C.sec }}>Authorization Risk</div>
            {context && context.auth_risk ? (
              <>
                <div style={{ fontSize: 28, fontWeight: 700, marginTop: 6, color: context.auth_risk.expired > 0 ? C.red : context.auth_risk.expiring_7d > 0 ? C.amber : C.ink }}>
                  {context.auth_risk.expired}<span style={{ fontSize: 14, fontWeight: 600, color: C.sec }}> lapsed, no renewal</span>
                </div>
                {context.auth_risk.dollars && context.auth_risk.expired > 0 && <div style={{ fontSize: 12, color: C.red, marginTop: 2, fontWeight: 600 }}>{fmtMoney(context.auth_risk.dollars.expired)} a week</div>}
                <div style={{ fontSize: 12, color: C.sec, marginTop: 2, fontWeight: 600 }}>{context.auth_risk.in_flight || 0} renewal in flight{context.auth_risk.dollars ? " \u00b7 " + fmtMoney(context.auth_risk.dollars.in_flight) : ""}</div>
                <div style={{ fontSize: 12, color: C.amber, marginTop: 2, fontWeight: 600 }}>{context.auth_risk.expiring_7d} expiring in 7 days{context.auth_risk.dollars ? " \u00b7 " + fmtMoney(context.auth_risk.dollars.expiring_7d) : ""}</div>
                <div style={{ fontSize: 12, color: C.sec, marginTop: 2 }}>{context.auth_risk.patients_affected} patients{context.auth_risk.dollars ? " \u00b7 " + fmtMoney(context.auth_risk.dollars.expired + context.auth_risk.dollars.in_flight + context.auth_risk.dollars.expiring_7d) + " a week" : " affected"}</div>
              </>
            ) : (
              <div style={{ fontSize: 28, fontWeight: 700, marginTop: 6 }}><V loading={loading} value={null} /></div>
            )}
            {nav && <button className="ei-link" style={{ marginTop: 6 }} onClick={() => nav("ur")}>Open UR -&gt;</button>}
          </div>
        </div>

        {/* ── TCIO EXECUTIVE BRIEF — rule-based insights from conditions already on screen */}
        {(() => {
          const insights = [];
          const go = (v) => nav ? () => nav(v) : null;

          // Occupancy vs target (org or facility scope)
          if (stats.haveData && target != null && stats.fill != null && stats.fill < target) {
            const worst = ["DTX","RTC","PHP","IOP"]
              .map(k => ({ k, ...stats.loc[k], fill: stats.loc[k].t > 0 ? (stats.loc[k].c / stats.loc[k].t) * 100 : null }))
              .filter(r => r.fill != null && r.fill < target)
              .sort((a, b) => a.fill - b.fill);
            insights.push({
              sev: stats.fill < target - 20 ? "red" : "amber",
              title: "Occupancy below target",
              body: `Current census is ${fmtPct(stats.fill)} vs ${fmtPct(target)} target.` +
                (worst.length ? ` ${worst.slice(0, 2).map(w => LOC_ACCENT[w.k].label).join(" and ")} account for most open capacity.` : ""),
              action: "View Bed Board", onClick: go("bedboard"),
            });
          }

          // Auth exposure (due-counts, already fetched)
          if (dueCounts && (dueCounts.auths_expiring_7d > 0 || dueCounts.auths_lapsed > 0)) {
            insights.push({
              sev: (dueCounts.auths_lapsed > 0 || dueCounts.auths_expiring_7d >= 20) ? "red" : "amber",
              title: "Authorization exposure",
              body: [dueCounts.auths_expiring_7d > 0 ? `${dueCounts.auths_expiring_7d} active patients have authorizations expiring within 7 days.` : null,
                dueCounts.auths_lapsed > 0 ? `${dueCounts.auths_lapsed} have lapsed with no renewal sent.` : null].filter(Boolean).join(" "),
              action: "Open UR", onClick: go("ur"),
            });
          }

          // Biggest fight/stalled payer (exec only)
          if (isExec && money && money.data) {
            const worstPayer = money.data
              .filter(p => (p.tier === 'fight' || p.tier === 'stalled') && (p.open_billed || 0) >= 100000)
              .sort((a, b) => (b.open_billed || 0) - (a.open_billed || 0))[0];
            if (worstPayer) {
              insights.push({
                sev: worstPayer.tier === 'fight' ? "red" : "amber",
                title: `${worstPayer.payer} ${worstPayer.tier === 'fight' ? 'fight' : 'stalled'}`,
                body: `${fmtMoney(worstPayer.open_billed)} billed with only ${fmtMoney(worstPayer.realistic_value)} realistic.`,
                action: "View Money", onClick: go("money"),
              });
            }
          }

          // Collections pace materially behind
          if (collections && collections.pace_vs_avg_pct !== null && collections.pace_vs_avg_pct < 50) {
            insights.push({
              sev: "amber",
              title: "Collections pace behind",
              body: `MTD collections are at ${collections.pace_vs_avg_pct}% of the trailing 3-month average.`,
              action: "View Collections", onClick: go("collections"),
            });
          }

          // Positive: goal beaten or a LOC at/above target
          if (funnel && funnel.admitted >= funnel.admit_goal) {
            insights.push({
              sev: "green",
              title: "Admissions ahead of goal",
              body: `${funnel.admitted} admits vs a goal of ${funnel.admit_goal} this period.`,
              action: "View Leads", onClick: go("leads"),
            });
          } else if (stats.haveData && target != null) {
            const best = ["DTX","RTC","PHP","IOP"]
              .map(k => ({ k, fill: stats.loc[k].t > 0 ? (stats.loc[k].c / stats.loc[k].t) * 100 : null }))
              .filter(r => r.fill != null && r.fill >= target)
              .sort((a, b) => b.fill - a.fill)[0];
            if (best) insights.push({
              sev: "green",
              title: `${LOC_ACCENT[best.k].label} performing well`,
              body: `${LOC_ACCENT[best.k].label} is at ${fmtPct(best.fill)} occupancy, at or above target.`,
              action: "View Bed Board", onClick: go("bedboard"),
            });
          }

          if (!insights.length) return null;
          const sevStyle = {
            red:   { dot: C.red,   bg: C.redBg },
            amber: { dot: C.amber, bg: C.amberBg },
            green: { dot: C.green, bg: C.greenBg },
          };
          return (
            <div className="ei-card" style={{ marginTop: 18, borderLeft: `3px solid ${C.purple}` }}>
              <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
                <div className="ei-panel-title" style={{ color: C.purple }}>TCIO Executive Brief</div>
                <div style={{ fontSize: 12, color: C.sec }}>{insights.length} area{insights.length > 1 ? "s" : ""} worth your attention</div>
              </div>
              <div className="ei-brief-grid">
                {insights.slice(0, 5).map((ins, i) => (
                  <div key={i} style={{ background: C.canvas, borderRadius: 8, padding: "12px 14px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ width: 8, height: 8, borderRadius: 4, background: sevStyle[ins.sev].dot, flexShrink: 0 }} />
                      <span style={{ fontSize: 13, fontWeight: 700 }}>{ins.title}</span>
                    </div>
                    <div style={{ fontSize: 12.5, color: C.sec, marginTop: 6, lineHeight: 1.45 }}>{ins.body}</div>
                    {ins.onClick && (
                      <button className="ei-link" style={{ marginTop: 8 }} onClick={ins.onClick}>{ins.action} -&gt;</button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })()}

        {/* ── ROW 2: CENSUS & CAPACITY + ADMISSIONS FUNNEL (deliberately adjacent) */}
        <div className="ei-row2">
          <div className="ei-card">
            <div className="ei-panel-title">Census &amp; Capacity</div>
            <div className="ei-panel-sub">Current occupancy by level of care</div>
            <table className="ei-tbl">
              <thead>
                <tr><th>Level of Care</th><th>Occupied</th><th>Open</th><th>Fill Rate</th><th>Target</th><th>Variance</th></tr>
              </thead>
              <tbody>
                {["DTX","RTC","PHP","IOP"].map(k => {
                  const cfg = LOC_ACCENT[k];
                  const row = stats.loc[k];
                  const fill = row.t > 0 ? (row.c / row.t) * 100 : null;
                  const bedsNeeded = (target != null && row.t > 0)
                    ? Math.ceil((target / 100) * row.t) - row.c : null;
                  return (
                    <tr key={k}>
                      <td>
                        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", padding: "3px 8px", borderRadius: 5, background: cfg.bg, color: cfg.color }}>{cfg.label}</span>
                      </td>
                      <td style={{ fontWeight: 700 }}>{row.t > 0 ? row.c : "--"}</td>
                      <td>{row.t > 0 ? row.t - row.c : "--"}</td>
                      <td style={{ fontWeight: 600 }}>{fmtPct(fill)}</td>
                      <td style={{ color: C.sec }}>{target != null ? fmtPct(target) : "--"}</td>
                      <td>
                        {bedsNeeded === null ? <span style={{ color: C.sec }}>--</span>
                          : bedsNeeded > 0
                            ? <span style={{ fontSize: 12, fontWeight: 700, padding: "3px 8px", borderRadius: 5, background: C.amberBg, color: C.amber }}>-{bedsNeeded} beds</span>
                            : <span style={{ fontSize: 12, fontWeight: 700, padding: "3px 8px", borderRadius: 5, background: C.greenBg, color: C.green }}>+{Math.abs(bedsNeeded)} over</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {context && context.forward && (context.forward.expected_admits_72h > 0 || context.forward.expected_dc_72h > 0) && (
              <div style={{ marginTop: 12, paddingTop: 10, borderTop: `1px solid ${C.border}` }}>
                <div style={{ display: "flex", gap: 16, fontSize: 12.5, color: C.sec, flexWrap: "wrap", alignItems: "center" }}>
                  <span>Census <b style={{ color: C.ink }}>{stats.pts}</b></span>
                  <span style={{ color: C.green }}>+{context.forward.expected_admits_72h} admits 72h</span>
                  {context.forward.expected_dc_72h > 0 && <span style={{ color: C.amber }}>-{context.forward.expected_dc_72h} DC 72h</span>}
                  <span style={{ fontWeight: 700, color: C.deep }}>Projected: {stats.pts + context.forward.expected_admits_72h - context.forward.expected_dc_72h}</span>
                </div>
              </div>
            )}
            {nav && <button className="ei-link" style={{ marginTop: 10 }} onClick={() => nav("bedboard")}>View Bed Board -&gt;</button>}
          </div>

          <div className="ei-card">
            <div className="ei-panel-title">Admissions Funnel ({periodDays} Days)</div>
            <div className="ei-panel-sub">From first contact to admission</div>
            {funnel ? (
              <>
                <div style={{ marginTop: 12 }}>
                  {[
                    { label: "Calls", n: funnel.calls },
                    { label: "Leads", n: funnel.leads },
                    { label: "VOBs", n: funnel.vobs },
                    { label: "Prescreens", n: funnel.prescreens },
                    { label: "Pending Admit", n: funnel.pending },
                    { label: "Admitted", n: funnel.admitted },
                  ].map((s, i, arr) => {
                    const max = arr[0].n || 1;
                    const prev = i > 0 ? arr[i - 1].n : null;
                    const conv = prev ? Math.round((s.n / prev) * 100) : null;
                    return (
                      <div key={s.label} style={{ display: "flex", alignItems: "center", gap: 10, padding: "5px 0" }}>
                        <span style={{ fontSize: 12, color: C.sec, width: 92, flexShrink: 0 }}>{s.label}</span>
                        <div style={{ flex: 1, height: 18, background: C.canvas, borderRadius: 4, overflow: "hidden" }}>
                          <div style={{ height: "100%", width: `${Math.max((s.n / max) * 100, 2)}%`, background: i === arr.length - 1 ? C.green : C.blue, borderRadius: 4 }} />
                        </div>
                        <span style={{ fontSize: 13, fontWeight: 700, width: 46, textAlign: "right" }}>{s.n.toLocaleString()}</span>
                        <span style={{ fontSize: 11, color: C.sec, width: 38, textAlign: "right" }}>{conv !== null ? conv + "%" : ""}</span>
                      </div>
                    );
                  })}
                </div>
                <div style={{ display: "flex", gap: 20, marginTop: 12, paddingTop: 12, borderTop: `1px solid ${C.border}`, flexWrap: "wrap" }}>
                  <div>
                    <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: C.sec }}>Call to Admit</div>
                    <div style={{ fontSize: 18, fontWeight: 700 }}>{funnel.calls > 0 ? ((funnel.admitted / funnel.calls) * 100).toFixed(1) + "%" : "--"}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: C.sec }}>Lead to Admit</div>
                    <div style={{ fontSize: 18, fontWeight: 700 }}>{funnel.leads > 0 ? ((funnel.admitted / funnel.leads) * 100).toFixed(1) + "%" : "--"}</div>
                  </div>
                  <div style={{ flex: 1, minWidth: 140 }}>
                    <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: C.sec }}>Goal</div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <div style={{ flex: 1, height: 8, background: C.canvas, borderRadius: 4, overflow: "hidden" }}>
                        <div style={{ height: "100%", width: `${Math.min((funnel.admitted / (funnel.admit_goal || 1)) * 100, 100)}%`, background: funnel.admitted >= funnel.admit_goal ? C.green : C.blue, borderRadius: 4 }} />
                      </div>
                      <span style={{ fontSize: 13, fontWeight: 700 }}>{funnel.admitted} / {funnel.admit_goal}</span>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div style={{ fontSize: 13, color: C.sec, marginTop: 14 }}>Funnel data unavailable</div>
            )}
          </div>
        </div>

        {/* ── ROW 3: REVENUE PERFORMANCE + CASH COLLECTIONS (projection is not cash) */}
        <div className="ei-row3">
          <div className="ei-card">
            <div className="ei-panel-title">Revenue Performance</div>
            <div className="ei-panel-sub" title="Projected from current census: payer mix x collected rates">Actual and projected revenue</div>
            <div className="ei-fin3">
              <div>
                <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: C.sec }}>Today</div>
                <div style={{ fontSize: 22, fontWeight: 700, marginTop: 4 }}>
                  <V loading={loading} value={stats.haveData && stats.rev ? stats.rev : null} render={v => fmtMoneyFull(v)} />
                </div>
                <div style={{ fontSize: 11.5, color: C.sec, marginTop: 2 }}>projected daily</div>
              </div>
              <div>
                <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: C.sec }}>30-Day Forecast</div>
                <div style={{ fontSize: 22, fontWeight: 700, marginTop: 4 }}>
                  <V loading={loading} value={stats.haveData && stats.rev ? stats.rev * 30 : null} render={v => fmtMoneyFull(v)} />
                </div>
                <div style={{ fontSize: 11.5, color: C.sec, marginTop: 2 }}>if census holds</div>
              </div>
              <div>
                <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: C.sec }}>Annual Run Rate</div>
                <div style={{ fontSize: 22, fontWeight: 700, marginTop: 4 }}>
                  <V loading={loading} value={stats.haveData && stats.rev ? stats.rev * 365 : null} render={v => fmtMoney(v)} />
                </div>
                <div style={{ fontSize: 11.5, color: C.sec, marginTop: 2 }}>based on current census</div>
              </div>
            </div>
            <div style={{ fontSize: 11.5, color: C.sec, marginTop: 12, paddingTop: 10, borderTop: `1px solid ${C.border}` }}>
              Based on current payer mix x collected payer rates
              {nav && <button className="ei-link" style={{ marginLeft: 12 }} onClick={() => nav("reports")}>View Reports -&gt;</button>}
            </div>
          </div>

          <div className="ei-card">
            <div className="ei-panel-title">Cash Collections</div>
            <div className="ei-panel-sub">Actual cash received</div>
            {collections ? (
              <>
                <div className="ei-fin3">
                  <div>
                    <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: C.sec }}>Collected MTD</div>
                    <div style={{ fontSize: 22, fontWeight: 700, marginTop: 4, color: C.deep }}>{fmtMoneyFull(collections.collected_mtd)}</div>
                    {collections.pace_vs_avg_pct !== null && (
                      <div style={{ fontSize: 11.5, marginTop: 2, color: collections.pace_vs_avg_pct >= 80 ? C.green : collections.pace_vs_avg_pct >= 50 ? C.amber : C.red }}>
                        {collections.pace_vs_avg_pct}% of trailing avg
                      </div>
                    )}
                  </div>
                  <div>
                    <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: C.sec }}>Collected YTD</div>
                    <div style={{ fontSize: 22, fontWeight: 700, marginTop: 4, color: C.deep }}>{fmtMoney(collections.collected_ytd)}</div>
                    <div style={{ fontSize: 11.5, color: C.sec, marginTop: 2 }}>January through today</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: C.sec }}>Trailing 3-Mo Avg</div>
                    <div style={{ fontSize: 22, fontWeight: 700, marginTop: 4, color: C.deep }}>{fmtMoneyFull(collections.trailing_3mo_avg)}</div>
                    <div style={{ fontSize: 11.5, color: C.sec, marginTop: 2 }}>monthly benchmark</div>
                  </div>
                </div>
                <div style={{ fontSize: 11.5, color: C.sec, marginTop: 12, paddingTop: 10, borderTop: `1px solid ${C.border}`, display: "flex", gap: 14, flexWrap: "wrap", alignItems: "center" }}>
                  <span>Last 30 days: <b style={{ color: C.ink }}>{fmtMoneyFull(collections.collected_last30)}</b></span>
                  {context && context.cash_forecast && context.cash_forecast.predicted_next_14d > 0 && (
                    <span title="TCIO forecast engine: latest frozen snapshot, unrealized weeks">
                      Predicted next 14d: <b style={{ color: C.green }}>{fmtMoneyFull(context.cash_forecast.predicted_next_14d)}</b>
                    </span>
                  )}
                  {nav && <button className="ei-link" onClick={() => nav("collections")}>View Collections -&gt;</button>}
                </div>
              </>
            ) : (
              <div style={{ fontSize: 13, color: C.sec, marginTop: 14 }}>Collections data unavailable</div>
            )}
          </div>
        </div>

        {/* ── ROW 4: A/R HEALTH + TOP PAYERS NEEDING ATTENTION (exec only) */}
        {isExec && (
          <div className="ei-row3">
            <div className="ei-card">
              <div className="ei-panel-title">A/R Health</div>
              <div className="ei-panel-sub">Accounts receivable and collection potential</div>
              {totals ? (
                <>
                  <div className="ei-fin3">
                    <div>
                      <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: C.sec }}>Open (Billed)</div>
                      <div style={{ fontSize: 22, fontWeight: 700, marginTop: 4 }}>{fmtMoney(totals.open_billed)}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: C.sec }}>Realistic Value</div>
                      <div style={{ fontSize: 22, fontWeight: 700, marginTop: 4, color: C.green }}>{fmtMoney(totals.realistic_value)}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: C.sec }}>Fight / Stalled</div>
                      <div style={{ fontSize: 22, fontWeight: 700, marginTop: 4, color: C.red }}>{fightStalled !== null ? fmtMoney(fightStalled) : "--"}</div>
                    </div>
                  </div>
                  {arSummary && arSummary.aging && arSummary.aging.length > 0 && (() => {
                    const ag = arSummary.aging;
                    const tot = ag.reduce((s, a) => s + parseFloat(a.billed || 0), 0) || 1;
                    const cols = { "0-30": C.green, "31-60": C.teal, "61-90": C.amber, "91-120": "#E07030", "120+": C.red };
                    return (
                      <div style={{ marginTop: 6 }}>
                        <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: C.sec, marginTop: 12 }}>A/R Aging</div>
                        <div className="ei-agebar">
                          {ag.map(a => (
                            <div key={a.age_bucket} style={{ width: `${(parseFloat(a.billed || 0) / tot) * 100}%`, background: cols[a.age_bucket] || C.soft }} title={`${a.age_bucket}: ${fmtMoney(a.billed)}`} />
                          ))}
                        </div>
                        <div className="ei-agelbl">
                          {ag.map(a => (
                            <div key={a.age_bucket} style={{ fontSize: 11.5, color: C.sec, marginRight: 12 }}>
                              <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: 2, background: cols[a.age_bucket] || C.soft, marginRight: 4 }} />
                              {a.age_bucket} <b style={{ color: C.ink }}>{fmtMoney(a.billed)}</b>
                            </div>
                          ))}
                        </div>
                        {arSummary.upload && arSummary.upload.upload_date && (
                          <div style={{ fontSize: 11, color: C.sec, marginTop: 8 }}>
                            Based on synced claims through {String(arSummary.upload.upload_date).slice(0, 10)}
                            {nav && <button className="ei-link" style={{ marginLeft: 12 }} onClick={() => nav("ar")}>View AR Details -&gt;</button>}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </>
              ) : (
                <div style={{ fontSize: 13, color: C.sec, marginTop: 14 }}>A/R data unavailable</div>
              )}
            </div>

            <div className="ei-card">
              <div className="ei-panel-title">Top Payers Needing Attention</div>
              <div className="ei-panel-sub">Highest financial impact exceptions</div>
              {money && money.data ? (() => {
                const exceptions = money.data
                  .filter(p => (p.tier === 'fight' || p.tier === 'stalled') && (p.open_billed || 0) >= 100000)
                  .sort((a, b) => (b.open_billed || 0) - (a.open_billed || 0))
                  .slice(0, 6);
                if (!exceptions.length) return <div style={{ fontSize: 13, color: C.green, marginTop: 14 }}>No payer exceptions over $100K</div>;
                return (
                  <table className="ei-tbl">
                    <thead>
                      <tr><th>Payer</th><th>Status</th><th style={{ textAlign: "right" }}>Billed</th><th style={{ textAlign: "right" }}>Realistic</th></tr>
                    </thead>
                    <tbody>
                      {exceptions.map(p => (
                        <tr key={p.payer}>
                          <td style={{ fontWeight: 600, maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.payer}</td>
                          <td>
                            <span className="ei-tier" style={p.tier === 'fight'
                              ? { background: C.redBg, color: C.red }
                              : { background: C.amberBg, color: C.amber }}>
                              {p.tier === 'fight' ? 'FIGHT' : 'STALLED'}
                            </span>
                          </td>
                          <td style={{ textAlign: "right", fontWeight: 600 }}>{fmtMoney(p.open_billed)}</td>
                          <td style={{ textAlign: "right", color: p.tier === 'fight' ? C.red : C.amber, fontWeight: 600 }}>{fmtMoney(p.realistic_value)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                );
              })() : (
                <div style={{ fontSize: 13, color: C.sec, marginTop: 14 }}>Payer data unavailable</div>
              )}
            </div>
          </div>
        )}

        {/* ── ROW 5: FACILITY PERFORMANCE (all-facilities mode; row click scopes dashboard) */}
        {facility === "all" && facilities.length > 1 && (
          <div className="ei-card" style={{ marginTop: 18 }}>
            <div className="ei-panel-title">Facility Performance</div>
            <div className="ei-panel-sub">Key metrics by facility - click a row to scope the dashboard</div>
            <div style={{ overflowX: "auto" }}>
              <table className="ei-tbl" style={{ minWidth: 640 }}>
                <thead>
                  <tr>
                    <th>Facility</th><th>Occupancy</th><th>Admits</th>
                    {isExec && <th style={{ textAlign: "right" }}>Collected MTD</th>}
                    <th>Auth Risk</th>
                    <th>Compliance Attention</th>
                  </tr>
                </thead>
                <tbody>
                  {facilities
                    .map(f => {
                      const s = snapshots[f.id]; const bs = (beds[f.id] || []).filter(b => b.bed_type !== 'program' && !b.is_overflow && (!b.bed_facility_id || b.bed_facility_id === f.id));
                      const pts = s ? (s.total_patients || 0) : null;
                      const tot = bs.length;
                      const occ = (pts !== null && tot > 0) ? (pts / tot) * 100 : null;
                      const adm = context && context.admits_by_facility
                        ? (context.admits_by_facility.find(a => a.facility_id === f.id) || {}).admits : null;
                      const coll = facColl ? (facColl.find(c => c.facility_id === f.id) || {}).mtd : null;
                      const a = attn[f.id];
                      const tgt = context && context.targets && context.targets[f.id] ? context.targets[f.id].occupancy_target : null;
                      const ar = context && context.auth_risk ? context.auth_risk.by_facility.find(x => x.facility_id === f.id) : null;
                      return { f, pts, tot, occ, adm, coll, a, tgt, ar };
                    })
                    .filter(r => r.tot > 0 || r.adm != null)
                    .sort((x, y) => (x.occ ?? 999) - (y.occ ?? 999))
                    .map(r => (
                      <tr key={r.f.id} onClick={() => setFacility(r.f.id)} style={{ cursor: "pointer" }}
                        onMouseEnter={e => e.currentTarget.style.background = C.canvas}
                        onMouseLeave={e => e.currentTarget.style.background = ""}>
                        <td style={{ fontWeight: 600 }}>{r.f.org_name}</td>
                        <td>
                          {r.occ === null ? <span style={{ color: C.sec }}>--</span> : (
                            <span style={{ fontWeight: 700, color: r.tgt != null ? (r.occ >= r.tgt ? C.green : r.occ >= r.tgt - 20 ? C.amber : C.red) : C.ink }}>
                              {fmtPct(r.occ)}
                            </span>
                          )}
                          {r.occ !== null && <span style={{ fontSize: 11.5, color: C.sec, marginLeft: 6 }}>{r.pts}/{r.tot}</span>}
                        </td>
                        <td style={{ fontWeight: 600 }}>{r.adm != null ? r.adm : <span style={{ color: C.sec }}>--</span>}</td>
                        {isExec && <td style={{ textAlign: "right", fontWeight: 600 }}>{r.coll != null ? fmtMoneyFull(r.coll) : <span style={{ color: C.sec }}>--</span>}</td>}
                        <td>
                          {r.ar && (r.ar.expired > 0 || r.ar.expiring_7d > 0)
                            ? <span style={{ fontSize: 12, fontWeight: 700, padding: "3px 8px", borderRadius: 5, background: r.ar.expired > 0 ? C.redBg : C.amberBg, color: r.ar.expired > 0 ? C.red : C.amber }}>
                                {r.ar.expired > 0 ? `${r.ar.expired} expired` : `${r.ar.expiring_7d} expiring`}
                              </span>
                            : <span style={{ fontSize: 12, fontWeight: 700, padding: "3px 8px", borderRadius: 5, background: C.greenBg, color: C.green }}>Clear</span>}
                        </td>
                        <td>
                          {r.a
                            ? (r.a.items > 0
                              ? <span style={{ fontSize: 12, fontWeight: 700, padding: "3px 8px", borderRadius: 5, background: C.amberBg, color: C.amber }}>{r.a.items} items / {r.a.patients} patients</span>
                              : <span style={{ fontSize: 12, fontWeight: 700, padding: "3px 8px", borderRadius: 5, background: C.greenBg, color: C.green }}>Clear</span>)
                            : <span style={{ fontSize: 12, color: C.sec }}>--</span>}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {facility !== "all" && (
          <div style={{ marginTop: 14 }}>
            <button className="ei-link" onClick={() => setFacility("all")}>Back to All Facilities</button>
          </div>
        )}

        {/* ── ROW 6: COMPLIANCE + UPCOMING RISK */}
        <div className="ei-row3">
          <div className="ei-card">
            <div className="ei-panel-title">Clinical &amp; Operational Compliance</div>
            <div className="ei-panel-sub">From the live compliance engines</div>
            {Object.keys(attn).length > 0 ? (() => {
              const totItems = Object.values(attn).reduce((s, a) => s + a.items, 0);
              const totPatients = Object.values(attn).reduce((s, a) => s + a.patients, 0);
              const scoped = facility === "all" ? { items: totItems, patients: totPatients } : (attn[facility] || { items: 0, patients: 0 });
              return (
                <div style={{ display: "flex", gap: 24, marginTop: 12, flexWrap: "wrap" }}>
                  <div>
                    <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: C.sec }}>Open Compliance Items</div>
                    <div style={{ fontSize: 22, fontWeight: 700, marginTop: 4, color: scoped.items > 0 ? C.amber : C.green }}>{scoped.items}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: C.sec }}>Patients Affected</div>
                    <div style={{ fontSize: 22, fontWeight: 700, marginTop: 4 }}>{scoped.patients}</div>
                  </div>
                </div>
              );
            })() : (
              <div style={{ fontSize: 13, color: C.sec, marginTop: 14 }}>No live-EMR compliance data in scope</div>
            )}
            <div style={{ fontSize: 11.5, color: C.sec, marginTop: 12, paddingTop: 10, borderTop: `1px solid ${C.border}` }}>
              Counts from the facility-scoped rule engine
              {nav && <button className="ei-link" style={{ marginLeft: 12 }} onClick={() => nav("emr")}>View Compliance -&gt;</button>}
            </div>
          </div>

          <div className="ei-card">
            <div className="ei-panel-title">Upcoming Risk</div>
            <div className="ei-panel-sub">What is likely to need attention soon</div>
            {(() => {
              const items = [];
              if (context && context.auth_risk) {
                const ar7 = context.auth_risk; const ard = ar7.dollars || null;
                if (ar7.expired > 0)
                  items.push({ n: ar7.expired, label: "authorizations lapsed, no renewal sent" + (ard ? " \u00b7 " + fmtMoney(ard.expired) + " a week" : ""), go: "ur" });
                if (ar7.expiring_7d > 0)
                  items.push({ n: ar7.expiring_7d, label: "authorizations expire within 7 days" + (ard ? " \u00b7 " + fmtMoney(ard.expiring_7d) + " a week" : ""), go: "ur" });
              }
              if (context && context.forward && context.forward.expected_dc_72h > 0)
                items.push({ n: context.forward.expected_dc_72h, label: "expected discharges next 72h", go: "bedboard" });
              if (context && context.forward && context.forward.expected_dc_7d > context.forward.expected_dc_72h)
                items.push({ n: context.forward.expected_dc_7d, label: "expected discharges next 7 days", go: "bedboard" });
              if (facility === "all" && context && context.targets && facilities.length > 1) {
                const below = facilities.filter(f => {
                  const s = snapshots[f.id]; const bsf = (beds[f.id] || []).filter(b => b.bed_type !== 'program' && !b.is_overflow && (!b.bed_facility_id || b.bed_facility_id === f.id));
                  if (!s || !bsf.length) return false;
                  const t = context.targets[f.id] ? context.targets[f.id].occupancy_target : null;
                  return t != null && ((s.total_patients || 0) / bsf.length) * 100 < t;
                }).length;
                if (below > 0) items.push({ n: below, label: "facilities below occupancy target", go: null });
              }
              if (!items.length) return <div style={{ fontSize: 13, color: C.green, marginTop: 14 }}>No forward-looking risks in scope</div>;
              return (
                <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
                  {items.map((it, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <span style={{ minWidth: 30, textAlign: "center", fontSize: 13, fontWeight: 800, padding: "2px 8px", borderRadius: 6, background: C.amberBg, color: C.amber }}>{it.n}</span>
                      <span style={{ fontSize: 13, flex: 1 }}>{it.label}</span>
                      {it.go && nav && <button className="ei-link" onClick={() => nav(it.go)}>View -&gt;</button>}
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        </div>

        {/* ── ROW 7: BILLED BY FACILITY - CENSUS vs ZEALIE */}
        {isExec && weeklyBilled && weeklyBilled.length > 0 && (() => {
          const M = { census: "#4479B0", zealie: "#18A6B8", expected: "#16A36A", gap: "#E5484D" };
          const fm = (n) => n == null ? "--" : "$" + (Math.abs(n) >= 1000000 ? (n / 1000000).toFixed(2) + "M" : Math.abs(n) >= 1000 ? Math.round(n / 1000).toLocaleString() + "K" : Math.round(n));
          const allWeeks = [...new Set(weeklyBilled.map(r => r.week_start))].sort();
          const weeks = allWeeks.slice(-bcWeeksN);
          const rows = (facility === "all" ? weeklyBilled : weeklyBilled.filter(r => r.facility_id === facility))
            .filter(r => weeks.includes(r.week_start) && (r.billed_value > 0 || r.pending_days > 0));
          if (!rows.length) return null;

          const byFac = {};
          rows.forEach(r => {
            if (!byFac[r.facility_id]) byFac[r.facility_id] = { facility_id: r.facility_id, org_name: r.org_name,
              billed: 0, expected: 0, zealie: 0, coveredBilled: 0, coveredWeeks: 0, missingWeeks: 0, weeks: [] };
            const f = byFac[r.facility_id];
            f.billed += r.billed_value || 0;
            f.expected += r.realistic_value || 0;
            f.weeks.push(r);
            if (r.zealie_billed != null) { f.zealie += r.zealie_billed; f.coveredBilled += r.billed_value || 0; f.coveredWeeks++; }
            else f.missingWeeks++;
          });
          const facs = Object.values(byFac).map(f => ({ ...f,
            gap: f.coveredWeeks ? Math.max(f.coveredBilled - f.zealie, 0) : null,
            coverage: f.coveredBilled > 0 ? Math.round((f.zealie / f.coveredBilled) * 100) : null,
            status: f.missingWeeks > 0 ? "missing" : (f.coveredBilled > 0 && (f.coveredBilled - f.zealie) > f.coveredBilled * 0.05 ? "review" : "ok"),
          })).sort((a, b) => (b.gap ?? -1) - (a.gap ?? -1) || b.billed - a.billed);
          const shownFacs = bcShowAll ? facs : facs.slice(0, 8);

          const totBilled = facs.reduce((s, f) => s + f.billed, 0);
          const totExpected = facs.reduce((s, f) => s + f.expected, 0);
          const anyCov = facs.some(f => f.coveredWeeks > 0);
          const totZ = anyCov ? facs.reduce((s, f) => s + f.zealie, 0) : null;
          const totGap = anyCov ? facs.reduce((s, f) => s + (f.gap || 0), 0) : null;
          const totMissing = facs.reduce((s, f) => s + f.missingWeeks, 0);

          // Horizontal bars, scaled within each group: census = 100% of track
          const hbar = (label, v, color, denom, dashed) => (
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
              <span style={{ width: 62, fontSize: 10.5, fontWeight: 700, color: C.sec, textAlign: "right", flexShrink: 0 }}>{label}</span>
              <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                {dashed
                  ? <div style={{ width: 46, height: 13, borderRadius: 4, border: `1.5px dashed ${C.border}`, flexShrink: 0 }} />
                  : <div style={{ width: `${Math.min(Math.max((v / denom) * 100, 0.8), 100)}%`, height: 13, background: color, borderRadius: 4 }} title={fm(v)} />}
                <span style={{ fontSize: 11, fontWeight: 700, color: dashed ? C.sec : "#22303E", whiteSpace: "nowrap" }}>{dashed ? "no data" : fm(v)}</span>
              </div>
            </div>
          );
          const statusChip = (s) => s === "missing"
            ? <span style={{ fontSize: 10.5, fontWeight: 700, padding: "3px 9px", borderRadius: 6, background: "#FFF6E5", color: "#E99A18" }}>Missing Data</span>
            : s === "review"
            ? <span style={{ fontSize: 10.5, fontWeight: 700, padding: "3px 9px", borderRadius: 6, background: "#FDECEE", color: "#E5484D" }}>Needs Review</span>
            : <span style={{ fontSize: 10.5, fontWeight: 700, padding: "3px 9px", borderRadius: 6, background: "#EAF8F1", color: "#16A36A" }}>On Track</span>;
          const pill = (label, value, sub, color, bg) => (
            <div key={label} style={{ background: bg || C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: "12px 16px", flex: "1 1 150px", minWidth: 150 }}>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: C.sec }}>{label}</div>
              <div style={{ fontSize: 20, fontWeight: 800, marginTop: 3, color }}>{value}</div>
              {sub && <div style={{ fontSize: 11, color: C.sec, marginTop: 1 }}>{sub}</div>}
            </div>
          );

          const openUnbilled = async (fid, ws) => {
            setUnbilled({ facility_id: fid, week_start: ws, loading: true, data: null });
            try {
              const r = await fetch(`${API}/api/billing-census/unbilled?facilityId=${fid}&weekStart=${ws}`, AUTH());
              const d = await r.json();
              setUnbilled({ facility_id: fid, week_start: ws, loading: false, data: d.ok ? d : null });
            } catch (e) { setUnbilled(null); }
          };
          const downloadXlsx = async (u, facName) => {
            if (!u || !u.data) return;
            const XLSX = await import('xlsx');
            const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
            const flagText = (fl) => ({ no_claim: "Authorized, not billed", retro_no_claim: "Retro bill - not billed", possible_member_id_mismatch: "Possible member ID mismatch - verify with Biller", loc_mismatch: "Claim level of care differs from UR - verify with Biller", other_facility: "Another facility billed the same day - verify with Biller", written_off: "Written off by Biller - appeal / rebill / accept", no_member_id: "No member ID on file" })[fl] || String(fl || "");
            const aoa = [
              [`Unbilled Days - ${facName} - Week of ${u.week_start}`],
              [`${u.data.summary.unbilled_days} days for Biller · $${Math.round(u.data.summary.unbilled_value).toLocaleString()} total` + (u.data.summary.truly_unbilled_days != null ? ` — ${u.data.summary.truly_unbilled_days} authorized & not billed ($${Math.round(u.data.summary.truly_unbilled_value || 0).toLocaleString()}), ${u.data.summary.verify_days || 0} to verify` : '')],
              [],
            ];
            (u.data.patients || []).forEach(p => {
              const dates = Array.isArray(p.unbilled_dates) ? p.unbilled_dates : (Array.isArray(p.dates) ? p.dates : []);
              const perDay = p.unbilled_days > 0 ? (p.unbilled_value || 0) / p.unbilled_days : 0;
              const idNote = p.flag === "possible_member_id_mismatch" && Array.isArray(p.ar_member_ids) && p.ar_member_ids.length
                ? `TCIO ID: ${p.member_id || "?"} / AR has: ${p.ar_member_ids.join(", ")}`
                : `Member ID: ${p.member_id || "none"}`;
              aoa.push([p.patient_name, p.loc || "", `Auth: ${p.auth_number || "n/a"}`, flagText(p.flag),
                `${p.unbilled_days} days`, p.unbilled_value > 0 ? p.unbilled_value : "no rate on file", idNote]);
              if (dates.length) {
                aoa.push(["", "Date", "Day", "LOC", "Day Rate", "Status"]);
                dates.forEach(d => {
                  const ds = String(d).slice(0, 10);
                  const dt = new Date(ds + "T12:00:00");
                  aoa.push(["", ds, DOW[dt.getDay()], p.loc || "", perDay > 0 ? Math.round(perDay) : "no rate", "NEEDS BILLING"]);
                });
              } else {
                aoa.push(["", `(${p.unbilled_days} unbilled days in week of ${u.week_start})`]);
              }
              aoa.push([]);
            });
            aoa.push(["TOTAL", "", "", "", `${u.data.summary.unbilled_days} days`, Math.round(u.data.summary.unbilled_value)]);
            const ws2 = XLSX.utils.aoa_to_sheet(aoa);
            ws2["!cols"] = [{ wch: 26 }, { wch: 13 }, { wch: 20 }, { wch: 34 }, { wch: 12 }, { wch: 16 }, { wch: 40 }];
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws2, "Unbilled Days");
            XLSX.writeFile(wb, `Unbilled_${facName.replace(/[^A-Za-z0-9]+/g, "_")}_${u.week_start}.xlsx`);
          };
          const modalFac = bcModal ? facs.find(f => f.facility_id === bcModal) : null;

          return (
            <div className="ei-card" style={{ marginTop: 16 }}>
              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                <div>
                  <div className="ei-panel-title">Billed by Facility - Census vs Biller</div>
                  <div className="ei-panel-sub">Census billable value, expected collectible, and Biller submissions by facility</div>
                </div>
                <select value={bcWeeksN} onChange={e => setBcWeeksN(Number(e.target.value))}
                  style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 12.5, padding: "6px 10px" }}>
                  <option value={4}>Last 4 Weeks</option>
                  <option value={8}>Last 8 Weeks</option>
                  <option value={allWeeks.length}>All Weeks</option>
                </select>
              </div>

              <div style={{ display: "flex", gap: 10, marginTop: 12, flexWrap: "wrap" }}>
                {pill("Census Billable Value", fm(totBilled), `Last ${weeks.length} weeks`, "#336090")}
                {pill("Expected Collectible", `${fm(totExpected)} · ${totBilled > 0 ? Math.round((totExpected / totBilled) * 100) : 0}%`, "of census billable value", M.expected, "#EAF8F1")}
                {pill("Biller Submitted", totZ != null ? fm(totZ) : "--", totZ != null && totBilled > 0 ? `${Math.round((totZ / totBilled) * 100)}% of census value` : "no coverage yet", "#22303E")}
                {totGap != null && totGap > 0 && pill("Unexplained Gap", fm(totGap), "billed minus submitted, covered weeks", M.gap, "#FDECEE")}
                {totMissing > 0 && pill("Missing Billing Data", `${totMissing} facility-weeks`, "No Biller submission data", C.sec, "#F1F4F8")}
              </div>

              <div style={{ display: "flex", gap: 8, marginTop: 14, alignItems: "center", flexWrap: "wrap" }}>
                {["facility", "week"].map(v => (
                  <button key={v} onClick={() => setBcView(v)}
                    style={{ background: bcView === v ? "#EAF1F8" : C.surface, border: `1px solid ${bcView === v ? "#4479B0" : C.border}`, borderRadius: 8, color: bcView === v ? "#336090" : C.sec, fontSize: 12.5, fontWeight: 700, padding: "6px 14px", cursor: "pointer" }}>
                    {v === "facility" ? "By Facility" : "By Week"}
                  </button>
                ))}
                <span style={{ flex: 1 }} />
                {[["Census Billable Value", M.census], ["Biller Submitted", M.zealie], ["Expected Collectible", M.expected], ["Unexplained Gap", M.gap]].map(([l, c2]) => (
                  <span key={l} style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11 }}>
                    <span style={{ width: 9, height: 9, borderRadius: 3, background: c2, display: "inline-block" }} />{l}
                  </span>
                ))}
              </div>

              {bcView === "facility" ? (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))", gap: "16px 28px", marginTop: 18 }}>
                  {shownFacs.map(f => {
                    const denom = Math.max(f.billed, f.zealie || 0, f.expected, f.gap || 0, 1);
                    return (
                      <div key={f.facility_id} onClick={() => { setBcModal(f.facility_id); setUnbilled(null); }}
                        style={{ cursor: "pointer", padding: "10px 12px", border: `1px solid ${C.border}`, borderRadius: 10 }}
                        title={`${f.org_name} - click for weekly detail`}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                          <span style={{ fontSize: 13, fontWeight: 800 }}>{f.org_name}</span>
                          <span style={{ flex: 1 }} />
                          {f.coverage != null
                            ? <span style={{ fontSize: 10.5, fontWeight: 700, padding: "2px 9px", borderRadius: 6, background: "#EAF1F8", color: "#336090" }}>{f.coverage}% submitted</span>
                            : null}
                          {f.missingWeeks > 0 &&
                            <span style={{ fontSize: 10.5, fontWeight: 700, padding: "2px 9px", borderRadius: 6, background: "#FFF6E5", color: "#E99A18" }}>{f.missingWeeks} missing wks</span>}
                        </div>
                        {hbar("Census", f.billed, M.census, denom)}
                        {f.coveredWeeks > 0 ? hbar("Biller", f.zealie, M.zealie, denom) : hbar("Biller", null, null, denom, true)}
                        {hbar("Expected", f.expected, M.expected, denom)}
                        {f.gap != null && f.gap > 0 && hbar("Gap", f.gap, M.gap, denom)}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))", gap: "16px 28px", marginTop: 18 }}>
                  {weeks.map(wk => {
                    const wr = rows.filter(r => r.week_start === wk);
                    const b = wr.reduce((s, r) => s + (r.billed_value || 0), 0);
                    const cov = wr.filter(r => r.zealie_billed != null);
                    const z = cov.length ? cov.reduce((s, r) => s + r.zealie_billed, 0) : null;
                    const e2 = wr.reduce((s, r) => s + (r.realistic_value || 0), 0);
                    const cb = cov.reduce((s, r) => s + (r.billed_value || 0), 0);
                    const g = cov.length ? Math.max(cb - z, 0) : null;
                    const denom = Math.max(b, z || 0, e2, g || 0, 1);
                    return (
                      <div key={wk} style={{ padding: "10px 12px", border: `1px solid ${C.border}`, borderRadius: 10 }}>
                        <div style={{ fontSize: 13, fontWeight: 800 }}>Week of {wk}</div>
                        {hbar("Census", b, M.census, denom)}
                        {z != null ? hbar("Biller", z, M.zealie, denom) : hbar("Biller", null, null, denom, true)}
                        {hbar("Expected", e2, M.expected, denom)}
                        {g != null && g > 0 && hbar("Gap", g, M.gap, denom)}
                      </div>
                    );
                  })}
                </div>
              )}

              <div style={{ marginTop: 18 }}>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: C.sec }}>Facility Breakdown (Last {weeks.length} Weeks)</div>
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5, marginTop: 8, minWidth: 820 }}>
                    <thead><tr style={{ textAlign: "left", color: C.sec, fontSize: 10.5, textTransform: "uppercase" }}>
                      <th style={{ padding: "6px 8px 6px 0" }}>Facility</th>
                      <th style={{ textAlign: "right" }}>Census Billable</th>
                      <th style={{ textAlign: "right" }}>Biller Submitted</th>
                      <th style={{ textAlign: "right" }}>Expected Collectible</th>
                      <th style={{ textAlign: "right" }}>Unexplained Gap</th>
                      <th style={{ textAlign: "right" }}>Coverage</th>
                      <th style={{ textAlign: "right" }}>Missing Wks</th>
                      <th>Status</th><th></th>
                    </tr></thead>
                    <tbody>
                      {shownFacs.map(f => (
                        <tr key={f.facility_id} onClick={() => { setBcModal(f.facility_id); setUnbilled(null); }} style={{ borderTop: `1px solid ${C.border}`, cursor: "pointer" }}>
                          <td style={{ padding: "8px 8px 8px 0", fontWeight: 700 }}>{f.org_name}</td>
                          <td style={{ textAlign: "right", fontWeight: 600 }}>{fm(f.billed)}</td>
                          <td style={{ textAlign: "right" }}>{f.coveredWeeks > 0 ? fm(f.zealie) : "--"}</td>
                          <td style={{ textAlign: "right", color: M.expected, fontWeight: 600 }}>{fm(f.expected)}</td>
                          <td style={{ textAlign: "right", color: f.gap > 0 ? M.gap : C.sec, fontWeight: 700 }}>{f.gap != null ? fm(f.gap) : "--"}</td>
                          <td style={{ textAlign: "right" }}>{f.coverage != null ? f.coverage + "%" : "--"}</td>
                          <td style={{ textAlign: "right" }}>{f.missingWeeks}</td>
                          <td>{statusChip(f.status)}</td>
                          <td style={{ color: C.sec }}>&#8250;</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {facs.length > 8 && (
                  <button className="ei-link" style={{ marginTop: 8 }} onClick={() => setBcShowAll(!bcShowAll)}>
                    {bcShowAll ? "Show Top 8" : `View All ${facs.length} Facilities`}
                  </button>
                )}
              </div>

              {modalFac && (
                <div onClick={() => { setBcModal(null); setUnbilled(null); }} style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", zIndex: 1300, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
                  <div onClick={e => e.stopPropagation()} style={{ background: "#fff", borderRadius: 14, padding: 22, width: "100%", maxWidth: 760, maxHeight: "85vh", overflowY: "auto", boxShadow: "0 24px 64px rgba(15,23,42,0.35)" }}>
                    <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
                      <span style={{ fontSize: 17, fontWeight: 800 }}>{modalFac.org_name}</span>
                      <span style={{ fontSize: 12.5, color: C.sec }}>Billing Reconciliation · Last {weeks.length} Weeks</span>
                      <span style={{ flex: 1 }} />
                      <button className="ei-link" onClick={() => { setBcModal(null); setUnbilled(null); }}>Close</button>
                    </div>
                    <div style={{ display: "flex", gap: 18, marginTop: 12, flexWrap: "wrap", fontSize: 12.5 }}>
                      <span>Census <b style={{ color: M.census }}>{fm(modalFac.billed)}</b></span>
                      <span>Biller <b style={{ color: M.zealie }}>{modalFac.coveredWeeks > 0 ? fm(modalFac.zealie) : "--"}</b></span>
                      <span>Expected <b style={{ color: M.expected }}>{fm(modalFac.expected)}</b></span>
                      {modalFac.gap != null && <span>Gap <b style={{ color: M.gap }}>{fm(modalFac.gap)}</b></span>}
                    </div>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5, marginTop: 12 }}>
                      <thead><tr style={{ textAlign: "left", color: C.sec, fontSize: 10.5, textTransform: "uppercase" }}>
                        <th style={{ padding: "5px 8px 5px 0" }}>Week</th>
                        <th style={{ textAlign: "right" }}>Census Value</th>
                        <th style={{ textAlign: "right" }}>Biller Submitted</th>
                        <th style={{ textAlign: "right" }}>Expected</th>
                        <th style={{ textAlign: "right" }}>Gap</th>
                        <th></th>
                      </tr></thead>
                      <tbody>
                        {modalFac.weeks.sort((a, b) => b.week_start.localeCompare(a.week_start)).map(w => {
                          const g = w.zealie_billed != null ? Math.max((w.billed_value || 0) - w.zealie_billed, 0) : null;
                          return (
                            <tr key={w.week_start} style={{ borderTop: `1px solid ${C.border}` }}>
                              <td style={{ padding: "7px 8px 7px 0", fontWeight: 700 }}>{w.week_start}</td>
                              <td style={{ textAlign: "right" }}>{fm(w.billed_value)}</td>
                              <td style={{ textAlign: "right" }}>{w.zealie_billed != null ? fm(w.zealie_billed) : <span style={{ color: C.sec }}>no data</span>}</td>
                              <td style={{ textAlign: "right", color: M.expected }}>{fm(w.realistic_value)}</td>
                              <td style={{ textAlign: "right", color: g > 0 ? M.gap : C.sec, fontWeight: 700 }}>{g != null ? fm(g) : "--"}</td>
                              <td style={{ textAlign: "right" }}>
                                <button className="ei-link" onClick={() => openUnbilled(modalFac.facility_id, w.week_start)}>Unbilled -&gt;</button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    {unbilled && unbilled.facility_id === modalFac.facility_id && (
                      <div style={{ marginTop: 14, borderTop: `1px solid ${C.border}`, paddingTop: 12 }}>
                        {unbilled.loading ? <div style={{ fontSize: 12.5, color: C.sec }}>Matching census days against claims...</div>
                        : !unbilled.data ? <div style={{ fontSize: 12.5, color: M.gap }}>Could not load</div>
                        : (
                          <>
                            <div style={{ display: "flex", gap: 10, alignItems: "baseline", flexWrap: "wrap" }}>
                              <span style={{ fontSize: 12.5, fontWeight: 800 }}>Unbilled - week of {unbilled.week_start}</span>
                              <span style={{ fontSize: 12, color: C.sec }}>{unbilled.data.summary.unbilled_days} days · <b style={{ color: M.gap }}>{fm(unbilled.data.summary.unbilled_value)}</b>{unbilled.data.summary.truly_unbilled_days != null && <> · <span style={{ color: M.gap, fontWeight: 700 }}>{unbilled.data.summary.truly_unbilled_days} not billed {fm(unbilled.data.summary.truly_unbilled_value || 0)}</span> · <span style={{ color: '#E99A18', fontWeight: 700 }}>{unbilled.data.summary.verify_days || 0} verify</span></>}</span>
                              <span style={{ flex: 1 }} />
                              <button className="ei-link" onClick={() => downloadXlsx(unbilled, modalFac.org_name)}>Download Excel</button>
                            </div>
                            {unbilled.data.patients.length === 0
                              ? <div style={{ fontSize: 12.5, color: M.expected, marginTop: 8, fontWeight: 700 }}>Every billable day matches a claim</div>
                              : unbilled.data.patients.map(p => (
                                <div key={p.episode_id} style={{ display: "flex", gap: 10, fontSize: 12, marginTop: 6, alignItems: "center", flexWrap: "wrap" }}>
                                  <b style={{ minWidth: 150 }}>{p.patient_name}</b>
                                  <span style={{ color: C.sec }}>{p.loc} · {p.unbilled_days} days</span>
                                  <span style={{ fontWeight: 700, color: p.unbilled_value > 0 ? M.gap : C.sec }}>{p.unbilled_value > 0 ? fm(p.unbilled_value) : "no rate"}</span>
                                  <span style={{ fontSize: 10, fontWeight: 700, padding: "1px 6px", borderRadius: 4,
                                    background: (p.flag === "no_claim" || p.flag === "retro_no_claim") ? "#FDECEE" : p.flag === "written_off" ? "#EEF0F3" : "#FFF6E5",
                                    color: (p.flag === "no_claim" || p.flag === "retro_no_claim") ? M.gap : p.flag === "written_off" ? "#5B6572" : "#E99A18" }}
                                    title={p.reason || ""}>
                                    {({ no_claim: "NOT BILLED", retro_no_claim: "RETRO", possible_member_id_mismatch: "ID MISMATCH?", loc_mismatch: "LOC DIFFERS?", other_facility: "OTHER FACILITY?", written_off: "WRITTEN OFF", no_member_id: "NO MEMBER ID" })[p.flag] || String(p.flag || "").toUpperCase()}
                                  </span>
                                </div>
                              ))}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })()}

        {/* Phase C6+ mounts below: drill-down modals + Executive Brief */}
        <div style={{ height: 32 }} />
      </div>
    </div>
  );
}
